import { hashKey, keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type {
  IssueLogPage,
  IssueLogQuery,
  IssuingAnalyticsQuery,
  IssuingAnalyticsResponse,
} from '@/lib/analytics';

/**
 * Exported the way `DASHBOARD_KEY` is, so a mutation that invalidates these
 * aggregates does not have to reach into this module for the prefix.
 *
 * Nothing invalidates it today, and that is a decision rather than an omission:
 * issuing happens on the inventory page, so an admin cannot change these numbers
 * while looking at them, and the `staleTime`s below cover the walk back. If
 * issuing ever moves onto this screen, invalidate this key from that mutation —
 * do not shorten the `staleTime` to paper over it.
 */
export const ANALYTICS_KEY = ['analytics', 'issuing'] as const;

/**
 * The window + filter fields both endpoints share, taken from the contract
 * rather than restated — `Pick` here means adding a filter to the contract
 * surfaces as a compile error in `appendWindow` instead of silently dropping
 * out of every request URL.
 */
type WindowQuery = Pick<IssuingAnalyticsQuery, 'from' | 'to' | 'itemTypes' | 'itemIds'>;

/**
 * Serialise the shared window/filter fields.
 *
 * `from`/`to` go on the wire **verbatim**. They are `yyyy-mm-dd` clinic-local
 * calendar dates, not instants, and the server owns the timezone — so there is
 * nothing here to normalise and a `toISOString()` anywhere near this function is
 * a bug, not a formatting choice. (An instant is a 400; see the doc comment on
 * `IssuingAnalyticsQuery`.) Note in particular that this is *not* the audit
 * log's `?from=...T23:59:59.999Z` pattern — that API takes instants, this one
 * does not.
 *
 * Lists go comma-separated, matching `commaSeparated()` in
 * `analytics.schemas.ts`. An empty list is omitted entirely rather than sent as
 * a blank value: a cleared filter means "everything", never "nothing", and the
 * two must not be spelled the same way.
 */
function appendWindow(search: URLSearchParams, query: WindowQuery): void {
  search.set('from', query.from);
  search.set('to', query.to);
  if (query.itemTypes?.length) search.set('itemTypes', query.itemTypes.join(','));
  if (query.itemIds?.length) search.set('itemIds', query.itemIds.join(','));
}

/**
 * A filter list as the query key should carry it: `[]` becomes `undefined`.
 *
 * The two already serialise identically — `appendWindow` omits both, because a
 * cleared filter means "everything" — but they hash differently, so a filter
 * cleared to `[]` would fetch the byte-identical URL again under a second cache
 * entry and blank the page while it did. Collapsing them here makes the key
 * agree with the URL it produces.
 */
function filterOrAll(list: readonly string[] | undefined): readonly string[] | undefined {
  return list?.length ? list : undefined;
}

/**
 * The hook params are the contract's query type plus the house `enabled`
 * escape hatch — nothing is re-declared, so a field the server learns to accept
 * is available here the moment the contract ships it.
 */
export type IssuingAnalyticsParams = IssuingAnalyticsQuery & { readonly enabled?: boolean };

/**
 * What a payload is *of* — a panels query key with `measure` taken out of it,
 * since the measure decides only which number is read out of a payload. Two keys
 * with the same slice identity cover the same clinic, window, buckets and
 * filters, so one's panels are a fair thing to leave on screen while the other
 * loads.
 *
 * Taken from the whole key, not just the query object: the active clinic lives
 * in the key, and another clinic's numbers must never appear under this one's
 * headings. And derived by hashing the remainder rather than by listing fields,
 * so a filter the contract adds joins the comparison the day it ships with no
 * edit here. (`hashKey` drops `undefined`, which is why `filterOrAll` above and
 * this function agree about a cleared filter.)
 */
function sliceIdentity(key: readonly unknown[]): string {
  const { measure: _measure, ...slice } = key.at(-1) as IssuingAnalyticsQuery;
  return hashKey([...key.slice(0, -1), slice]);
}

/**
 * The four-panel issuing payload: one request feeds totals, trend, by-type and
 * top-items, because they are separate aggregates over the same window and the
 * page has no state in which it wants three of the four.
 *
 * Gated server-side on `analytics.read`. We don't pre-check the
 * role on the client — the route guard already keeps non-admins off the screen,
 * so a staff caller reaching this hook is a bug worth surfacing as a 403 rather
 * than hiding as an idle query. Same reasoning as `useAuditLogs`.
 *
 * Keyed by active clinic, so the numbers can't survive a clinic switch.
 *
 * `placeholderData` is conditional rather than a flat `keepPreviousData` — see
 * the note on the option itself.
 */
export function useIssuingAnalytics(params: IssuingAnalyticsParams) {
  const { enabled = true, ...rest } = params;
  const query: IssuingAnalyticsQuery = {
    ...rest,
    itemTypes: filterOrAll(rest.itemTypes),
    itemIds: filterOrAll(rest.itemIds),
  };
  const { activeClinic, isAuthenticated } = useAuth();
  // Named because `placeholderData` compares against it; the query object stays
  // last in the key, which is what `sliceIdentity` reads.
  const queryKey = [...ANALYTICS_KEY, 'panels', activeClinic?.id ?? null, query];
  return useQuery({
    queryKey,
    queryFn: () => {
      const search = new URLSearchParams();
      appendWindow(search, query);
      search.set('bucket', query.bucket);
      // Decides the ranked panel's sort order, which the server does — so this
      // is part of the key and the Units/Issues toggle is a refetch, not a
      // re-render. The other three panels carry both measures and don't need it;
      // `top_items` is why the parameter exists at all.
      search.set('measure', query.measure);
      return clinicApi.get<IssuingAnalyticsResponse>(`analytics/issuing?${search.toString()}`);
    },
    enabled: enabled && isAuthenticated && !!activeClinic,
    staleTime: 30_000,
    // Because of that refetch, toggling the measure would otherwise blank all
    // four panels to a skeleton — a whole-page reload to answer "what does this
    // same window look like in issues instead of units?". So the previous
    // payload stays on screen while the new one lands, but *only* when the slice
    // is identical. A new window, bucket, filter or clinic returns `undefined`
    // and reads as the fresh load it is, rather than showing last month's series
    // under this month's heading. Callers can dim on `isPlaceholderData`.
    placeholderData: (previous, previousQuery) =>
      previousQuery && sliceIdentity(previousQuery.queryKey) === sliceIdentity(queryKey)
        ? previous
        : undefined,
  });
}

/**
 * Paging is the contract's `limit`/`offset` rather than a `page`/`pageSize`
 * pair, so this module keeps its "the query type is the contract's query type"
 * property. Turning a page number into an offset is presentation arithmetic and
 * belongs to the component that owns the page number.
 */
export type IssueLogParams = IssueLogQuery & { readonly enabled?: boolean };

/**
 * The raw ledger lines behind the aggregates — the drill-down an admin opens to
 * check a number.
 *
 * Paginated separately from the panels on purpose: changing log page must not
 * refetch four aggregates. `keepPreviousData` because paging returns the same
 * data under the same headings, so flashing an empty table between pages would
 * be noise; the window and filters are shared with the panels above, so the
 * rows stay an honest expansion of whatever the charts are showing.
 *
 * `limit`/`offset` are omitted from the URL when undefined — the server's
 * defaults (25 / 0) are the single source of truth for them.
 */
export function useIssueLog(params: IssueLogParams) {
  const { enabled = true, ...rest } = params;
  const query: IssueLogQuery = {
    ...rest,
    itemTypes: filterOrAll(rest.itemTypes),
    itemIds: filterOrAll(rest.itemIds),
  };
  const { activeClinic, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [...ANALYTICS_KEY, 'log', activeClinic?.id ?? null, query],
    queryFn: () => {
      const search = new URLSearchParams();
      appendWindow(search, query);
      if (query.limit !== undefined) search.set('limit', String(query.limit));
      if (query.offset !== undefined) search.set('offset', String(query.offset));
      return clinicApi.get<IssueLogPage>(`analytics/issuing/log?${search.toString()}`);
    },
    enabled: enabled && isAuthenticated && !!activeClinic,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}
